(function() {
    'use strict';
    /*
     * Copyright (c) 2015 In Hand Health.
     * All rights reserved.
     */

    /**
     * A callback to report an asynchronous successful operation on a view/edit object.
     * @callback SuccessFn
     * @ngdoc callback
     * @name SuccessFn
     * @memberOf ihh-common-web.ViewEditCtrlBase
     * @param {Object} object the view/edit object just successfully operated on
     */

    /**
     * A callback to report an asynchronous error operating on a view/edit object.
     * @callback ErrorFn
     * @ngdoc callback
     * @name ErrorFn
     * @memberOf ihh-common-web.ViewEditCtrlBase
     * @param {Object|String} err a REST error object, or an error string
     */

    /**
     * The service that provides the **ViewEditCtrlBase** controller constructor function.
     * @ngdoc service
     * @name ViewEditCtrlBaseService
     * @memberOf ihh-common-web
     */
    angular
        .module('ihh-common-web')
        .service('ViewEditCtrlBaseService', ViewEditCtrlBaseService);

    ViewEditCtrlBaseService.$inject = ['Utilities', 'RestResources'];

    function ViewEditCtrlBaseService(Utilities, RestResources) {
        /**
         * A base implementation of standard view/edit functionality for a controller that implements a page for maintaining a record.
         * The derived class must implement the documented methods below, call the constructor, and set is prototype to inherit from this
         * function.
         *
         * The derived controller implements some methods to give access to a view mode object and an edit mode object. This base controller
         * manages transitions between view and edit mode, warns before losing changes by leaving a dirty page, and provides scope methods
         * to attach to *Edit*, *Save*, *Cancel*, and *Delete* buttons.
         * @ngdoc controller
         * @name ViewEditCtrlBase
         * @memberOf ihh-common-web
         */
        function ViewEditCtrlBase($scope, isNewObject) {
            var self = this;
            var isNew = isNewObject;

            this.setEdit = function(edit) {
                $scope.isEdit = edit;
            };

            $scope.edit = function() {
                self.setTempObject(angular.copy(self.getObject()));
                self.setEdit(true);
                $scope[self.getFormName()].$setPristine();
            };

            $scope.setDirty = function() {
                $scope[self.getFormName()].$setDirty();
            };

            $scope.save = function() {
                if (isNew) {
                    self.createObject(self.getTempObject(), function(result) {
                        isNew = false;
                        self.setObject(result);
                        self.setEdit(false);
                        self.setListDirty();
                        $scope[self.getFormName()].$setPristine();
                    }, function(err) {
                        RestResources.reportRestError('Could not create ' + self.getObjectName(), err);
                    });
                }
                else {
                    self.updateObject(self.getTempObject(), function(result) {
                        self.setObject(result);
                        self.setEdit(false);
                        self.setListDirty();
                        $scope[self.getFormName()].$setPristine();
                    }, function(err) {
                        RestResources.reportRestError('Could not update ' + self.getObjectName(), err);
                    });
                }
            };

            $scope.cancel = function() {
                $scope[self.getFormName()].$setPristine();
                if (isNew) {
                    $scope.back();
                }
                else {
                    self.setTempObject({});
                    self.setEdit(false);
                }
            };

            $scope.del = function() {
                Utilities.confirmDialog('Are you sure you want to permanently delete this ' + self.getObjectName() + '?', function() {
                    self.deleteObject(self.getObject(), function() {
                        self.setListDirty();
                        $scope[self.getFormName()].$setPristine();
                        $scope.back();
                    }, function(err) {
                        RestResources.reportRestError('Could not delete ' + self.getObjectName(), err);
                    });
                });
            };

            this.getIsNew = function() {
                return isNew;
            };
        }

        /**
         * Implemented by the base controller, and is not intended to be overridden. Initializes the view/edit framework, handling both
         * creating a new object and editing an existing object (as specified by the **isNewObject** argument of the constructor. Must
         * be called after the constructor and after the derived prototype is set.
         * @ngdoc method
         * @name init
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {Object} $scope the scope of the controller
         */
        ViewEditCtrlBase.prototype.init = function($scope) {
            var self = this;
            if (self.getIsNew()) {
                self.setEdit(true);
                self.setTempObject(self.newObject(function(result) {
                    self.initObject(result);
                }, function(err) {
                    RestResources.reportRestError('Could not get ' + self.getObjectName(), err);
                }));
            }
            else {
                self.setEdit(false);
                self.readObject(function(result) {
                    self.setObject(result);
                }, function(err) {
                    RestResources.reportRestError('Could not read ' + self.getObjectName(), err);
                });
            }

            var unWatch = $scope.$watch(self.getFormName(), function(form) {
                if (form) {
                    Utilities.confirmAway($scope, $scope[self.getFormName()]);
                    unWatch();
                }
            });
        };

        /**
         * Implemented by the derived controller, returns the name of the object being edited (for labeling and dialogs).
         * @ngdoc method
         * @name getObjectName
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @return {String} the name of the object being edited
         */
        ViewEditCtrlBase.prototype.getObjectName = function() {
            throw new Error('Must define getObjectName');
        };

        /**
         * Implemented by the derived controller, returns the view mode object.
         * @ngdoc method
         * @name getObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @return {Object} the view mode object
         */
        ViewEditCtrlBase.prototype.getObject = function() {
            throw new Error('Must define getObject');
        };

        /**
         * Implemented by the derived controller, sets the view mode object.
         * @ngdoc method
         * @name setObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {Object} object the view mode object to set
         */
        ViewEditCtrlBase.prototype.setObject = function(/* object */) {
            throw new Error('Must define setObject');
        };

        /**
         * Implemented by the derived controller, returns the edit mode (temporary) object.
         * @ngdoc method
         * @name getTempObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @return {Object} the edit mode (temporary) object
         */
        ViewEditCtrlBase.prototype.getTempObject = function() {
            throw new Error('Must define getTempObject');
        };

        /**
         * Implemented by the derived controller, sets the edit mode (temporary) object.
         * @ngdoc method
         * @name setTempObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {Object} object the edit mode (temporary) object to set
         */
        ViewEditCtrlBase.prototype.setTempObject = function(/* object */) {
            throw new Error('Must define setTempObject');
        };

        /**
         * Implemented by the derived controller, creates a new (unsaved) object to become the edit mode object.
         * @ngdoc method
         * @name newObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {SuccessFn} successFn called on success with the newly created object
         * @param {ErrorFn} errFn called on error
         */
        ViewEditCtrlBase.prototype.newObject = function(/* successFn, errFn */) {
            throw new Error('Must define newObject');
        };

        /**
         * Implemented by the derived controller, performs any necessary initialization on a newly created object. Will be preceded by
         * a call to **newObject**.
         * @ngdoc method
         * @name initObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {type} object the newly created object
         */
        ViewEditCtrlBase.prototype.initObject = function(object) {
            // Do nothing...optional override
            object = null;
            return object;
        };

        /**
         * Implemented by the derived controller, creates a new object on the server.
         * @ngdoc method
         * @name createObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {type} data the object data to send to the server
         * @param {SuccessFn} successFn called on success with the newly created object
         * @param {ErrorFn} errFn called on error
         */
        ViewEditCtrlBase.prototype.createObject = function(/* data, successFn, errFn */) {
            throw new Error('Must define createObject');
        };

        /**
         * Implemented by the derived controller, reads the view object from the server.
         * @ngdoc method
         * @name readObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {SuccessFn} successFn called on success with the object from the server
         * @param {ErrorFn} errFn called on error
         */
        ViewEditCtrlBase.prototype.readObject = function(/* successFn, errFn */) {
            throw new Error('Must define readObject');
        };

        /**
         * Implemented by the derived controller, updates an object on the server.
         * @ngdoc method
         * @name updateObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {type} data the object data to send to the server
         * @param {SuccessFn} successFn called on success with the object from the server
         * @param {ErrorFn} errFn called on error
         */
        ViewEditCtrlBase.prototype.updateObject = function(/* data, successFn, errFn */) {
            throw new Error('Must define updateObject');
        };

        /**
         * Implemented by the derived controller, deletes an object on the server.
         * @ngdoc method
         * @name deleteObject
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @param {type} data the object data specifying the object to delete
         * @param {SuccessFn} successFn called on success
         * @param {ErrorFn} errFn called on error
         */
        ViewEditCtrlBase.prototype.deleteObject = function(/* data, successFn, errFn */) {
            throw new Error('Must define deleteObject');
        };

        /**
         * Implemented by the derived controller, returns the form name to use for validation and dirty checks.
         * @ngdoc method
         * @name getFormName
         * @memberOf ihh-common-web.ViewEditCtrlBase
         * @return {Object} the form name
         */
        ViewEditCtrlBase.prototype.getFormName = function() {
            throw new Error('Must define getFormName');
        };

        /**
         * Implemented by the derived controller, and called when a view/edit object is created, deleted, or updated. It is intended to
         * allow the derived controller to dirty a list of such objects if one is being maintained.
         * @ngdoc method
         * @name setListDirty
         * @memberOf ihh-common-web.ViewEditCtrlBase
         */
        ViewEditCtrlBase.prototype.setListDirty = function() {
            throw new Error('Must define setListDirty');
        };

        /**
         * Returns the constructor function for **ViewEditCtrlBase**. Used by derived controllers to get the base constructor to call.
         * @ngdoc method
         * @name get
         * @memberOf ihh-common-web.ViewEditCtrlBaseService
         * @returns {Function} the constructor function for **ViewEditCtrlBase**
         */
        this.get = function() {
            return ViewEditCtrlBase;
        };
    }
})();